"use client"

import { SectionBannerSidebarProps } from "@/(admin)/modules/ui-builder/config/themes/tamda-media/SectionBannerSidebar"
import { SectionSkeleton } from "@/(admin)/modules/ui-builder/config/themes/tamda-media/TamdaMediaNewsLayout/skeleton/SectionSkeleton"
import { resolveBatchDataEntry, useBatchDataMap } from "@/lib/batch-data-context"
import { buildTamdaMediaSectionBannerSidebarBannersKey } from "@/lib/extract-batch-requests"
import { ComponentConfig } from "@measured/puck"
import { useEffect, useRef, useState } from "react"
import { twMerge } from "tailwind-merge"
import { BannerData } from "../../../../../../../types/types"
import BannerCard from "../../../components/BannerCard"
import { generateResponsiveCSS } from "../../../lib/helper"
import useIsMobile from "../../../lib/use-is-mobile"
import { NewsSection } from "../TamdaMediaNewsLayout/components/NewsSection"
import { useNewsSections } from "../TamdaMediaNewsLayout/hooks/useNewsSections"
import { useStableComponentId } from "../TamdaMediaNewsLayout/hooks/useStableComponentId"

export const RenderConfig: ComponentConfig<SectionBannerSidebarProps> = {
  render: ({ className, styles, responsiveType, categoryIds, bannerIds, id: componentId, banner }) => {
    const id = useStableComponentId(componentId)
    const responsiveCSS = generateResponsiveCSS(id, styles || {}, responsiveType)
    const isMobile = useIsMobile()
    const { sections, loading: sectionsLoading } = useNewsSections(categoryIds, id)

    const normalizedBannerIds = (bannerIds || [])
      .map((b: { bannerId: string }) => b?.bannerId)
      .filter(Boolean) as string[]
    const batchData = useBatchDataMap()
    const bannerEntry = resolveBatchDataEntry<{ data?: BannerData[] }>(batchData, {
      key: buildTamdaMediaSectionBannerSidebarBannersKey(id),
      prefix: "tamda-media-section-banner-sidebar:",
      suffix: ":banners",
    })
    const allBanners = Array.isArray(bannerEntry?.data) ? bannerEntry.data : []
    const idIndex = new Map(normalizedBannerIds.map((bid, i) => [bid, i]))
    const banners = allBanners.sort((a, b) => (idIndex.get(String(a.id)) ?? 0) - (idIndex.get(String(b.id)) ?? 0))
    const bannersLoading = normalizedBannerIds.length > 0 && banners.length === 0
    const showMobileMap = new Map((bannerIds || []).map((b: { bannerId: string; showMobile?: boolean }) => [b?.bannerId, b?.showMobile]))
    const visibleBanners = isMobile ? banners.filter(b => showMobileMap.get(String(b.id))) : banners
    const contentRef = useRef<HTMLDivElement>(null)
    const [sidebarHeight, setSidebarHeight] = useState<number>()

    useEffect(() => {
      const contentEl = contentRef.current
      if (!contentEl) return

      const updateHeight = () => {
        const nextHeight = contentEl.getBoundingClientRect().height
        setSidebarHeight(prev => {
          if (prev === undefined || Math.abs(prev - nextHeight) > 1) {
            return nextHeight
          }
          return prev
        })
      }

      updateHeight()

      let resizeObserver: ResizeObserver | undefined
      if (typeof ResizeObserver !== "undefined") {
        resizeObserver = new ResizeObserver(() => updateHeight())
        resizeObserver.observe(contentEl)
      }

      window.addEventListener("resize", updateHeight)

      return () => {
        resizeObserver?.disconnect()
        window.removeEventListener("resize", updateHeight)
      }
    }, [])

    return (
      <>
        {responsiveCSS}
        <div className={twMerge(className)} id={id}>
          <div className="mx-auto max-w-[1160px] px-4">
            <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] sm:items-stretch">
              <div ref={contentRef} className="md:border-r border-black/20">
                {sectionsLoading
                  ? Array.from({ length: 2 }).map((_, idx) => <SectionSkeleton key={`section-skeleton-${idx}`} />)
                  : sections.map(s => <NewsSection key={s.categoryId} data={s} />)}
              </div>

              <div className="flex min-h-0 w-full">
                {bannersLoading ? (
                  <div className="w-full flex flex-col gap-5">
                    {Array.from({ length: 2 }).map((_, idx) => (
                      <div key={idx} className="h-48 w-full rounded bg-neutral-200 animate-pulse" />
                    ))}
                  </div>
                ) : (
                  <div className="h-full w-full flex flex-col gap-2 sm:pl-5">
                    {visibleBanners.map(b => (
                      <BannerCard key={b.id} banner={b} isMobile={isMobile} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    )
  },
}
